<script setup>
// Переход на Composition API
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useScrollLock } from '@/composables/useScrollLock'
import { getAuthorBrandOverride } from '@/config/authorBranding'

const route = useRoute()

defineProps({
  isDarkTheme: {
    type: Boolean,
    default: false,
  },
})
defineOptions({ name: 'AppNavbar' })

const isMobileMenuOpen = ref(false)
const fade = ref(false)

const logoText = ref('CHAPTER FOUR PRODUCTION')
const { lock, unlock } = useScrollLock()

const updateLogoText = () => {
  logoText.value = window.innerWidth < 424 ? 'CHAPTER FOUR' : 'CHAPTER FOUR PRODUCTION'
}

// Временная кастомизация: на страницах отдельных авторов логотип заменяется на их бренд
// и ведёт на страницу автора вместо главной.
const authorBrand = computed(() => getAuthorBrandOverride(route.name, route.params))
const displayLogoText = computed(() => authorBrand.value?.text || logoText.value)
const logoTo = computed(() => authorBrand.value?.to || '/')

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  isMobileMenuOpen.value ? lock() : unlock()
}

const handleScroll = () => {
  fade.value = window.scrollY > 50
}

const showProposalLink = computed(() => route.name !== 'author' && route.name !== 'author-kp')

watch(
  () => route.path,
  () => {
    if (isMobileMenuOpen.value) {
      isMobileMenuOpen.value = false
      unlock()
    }
  },
)

onMounted(() => {
  updateLogoText()
  window.addEventListener('resize', updateLogoText)
  window.addEventListener('scroll', handleScroll)
  handleScroll()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateLogoText)
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <nav
    id="navbar"
    :class="{
      fade: fade,
      'light-text': isDarkTheme,
    }"
  >
    <div class="left-menu" v-if="!isMobileMenuOpen">
      <router-link to="/projects" class="text-sm">ПРОЕКТЫ</router-link>
      <router-link to="/about" class="text-sm">УЧАСТНИКИ</router-link>
    </div>

    <router-link :to="logoTo" class="navbar-logo">{{ displayLogoText }}</router-link>

    <div class="right-menu" v-if="!isMobileMenuOpen && showProposalLink">
      <router-link to="/kp" class="text-sm">КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ</router-link>
    </div>

    <button class="hamburger burger-button" @click="toggleMobileMenu" aria-label="Меню">
      <span class="bar"></span>
      <span class="bar"></span>
      <span class="bar"></span>
    </button>

    <div
      class="mobile-menu"
      :class="{ 'is-open': isMobileMenuOpen }"
      role="dialog"
      aria-modal="true"
    >
      <nav class="mobile-menu-content">
        <router-link to="/projects" class="mobile-menu-item" @click="toggleMobileMenu">
          <span class="direction-title">ПРОЕКТЫ</span>
          <span class="arrow">→</span>
        </router-link>
        <router-link to="/about" class="mobile-menu-item" @click="toggleMobileMenu">
          <span class="direction-title">УЧАСТНИКИ</span>
          <span class="arrow">→</span>
        </router-link>
        <router-link
          v-if="showProposalLink"
          to="/kp"
          class="mobile-menu-item proposal-item"
          @click="toggleMobileMenu"
        >
          <span class="direction-title proposal-title">
            <span>КОММЕРЧЕСКОЕ</span>
            <span class="proposal-line">
              <span>ПРЕДЛОЖЕНИЕ</span>
              <span class="arrow">→</span>
            </span>
          </span>
        </router-link>
      </nav>
      <button class="close-button" @click="toggleMobileMenu" aria-label="Закрыть меню">
        <span></span>
        <span></span>
      </button>
    </div>
  </nav>
</template>

<style scoped>
#navbar {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  height: 5rem;
  z-index: 500;
  transition: all 0.3s ease;
  width: 100%;
  padding: 0 4rem;
  background-color: transparent;
  max-width: 1524px;
  margin: 0 auto;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
}

#navbar::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 5rem;
  background-color: var(--color-bg-primary);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  z-index: -1;
  pointer-events: none;
  transition:
    background-color 0.3s ease,
    backdrop-filter 0.3s ease,
    -webkit-backdrop-filter 0.3s ease;
}

#navbar.light-text::before {
  background-color: var(--color-bg-dark);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

#navbar.fade::before {
  background-color: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

#navbar.fade.light-text::before {
  background-color: rgba(18, 18, 18, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.left-menu,
.right-menu {
  display: flex;
  gap: clamp(1.25rem, 3vw, 2.5rem);
}

.left-menu {
  justify-self: start;
}

.right-menu {
  justify-self: end;
}

.right-menu a {
  text-align: right;
}

.navbar-logo {
  box-sizing: border-box;
  cursor: pointer;
  display: inline-block;
  font-family: Helvetica, sans-serif;
  font-size: clamp(1.25rem, 2vw, 1.5rem);
  font-weight: 700;
  height: 1.5rem;
  letter-spacing: normal;
  line-height: 1.5rem;
  text-align: center;
  text-decoration: none;
  -webkit-font-smoothing: antialiased;
  text-transform: uppercase;
}

#navbar a,
#navbar .navbar-logo {
  color: var(--color-text-primary);
  margin: 0;
}

#navbar.light-text a,
#navbar.light-text .navbar-logo {
  color: var(--color-white);
}

.left-menu a,
.right-menu a {
  position: relative;
}

.left-menu a::after,
.right-menu a::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 0.063rem;
  bottom: -0.25rem;
  left: 50%;
  background-color: var(--color-primary);
  transition: all 0.3s ease;
  transform: translateX(-50%) scaleX(0);
}

.left-menu a:hover::after,
.right-menu a:hover::after {
  transform: translateX(-50%) scaleX(1);
}

#navbar.light-text .left-menu a::after,
#navbar.light-text .right-menu a::after {
  background-color: #ffffff;
}

.hamburger span {
  background-color: var(--color-primary);
}

#navbar.light-text .hamburger span {
  background-color: #ffffff;
}

.text-sm {
  font-family: Helvetica, Arial, sans-serif;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.038rem;
  line-height: 1rem;
}

.hamburger {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 1.25rem;
  height: 0.938rem;
  cursor: pointer;
  background: transparent;
  border: none;
  justify-self: end;
  grid-column: 3;
  margin-bottom: 0.188rem;
}

.bar {
  display: block;
  height: 0.188rem;
  width: 100%;
  background-color: var(--color-primary);
  transition: all 0.3s ease;
}

.mobile-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: var(--color-bg-dark);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  visibility: hidden;
  transition: 0.3s;
}

.mobile-menu.is-open {
  opacity: 1;
  visibility: visible;
}

.mobile-menu-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.25rem;
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: white;
  font-size: 1.5rem;
  line-height: 1.75rem;
}

.proposal-title {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.proposal-line {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.mobile-menu-item span {
  color: white;
}

.arrow {
  margin-left: 0.313rem;
  font-size: 1.5em;
  font-weight: bold;
}

.close-button {
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  width: 1.875rem;
  height: 1.875rem;
  cursor: pointer;
  background: transparent;
}

.close-button span {
  display: block;
  height: 0.188rem;
  width: 100%;
  background-color: white;
  position: absolute;
  top: 50%;
}

.close-button span:first-child {
  transform: rotate(45deg);
}

.close-button span:last-child {
  transform: rotate(-45deg);
}

@media (max-width: 64rem) {
  #navbar {
    padding: 0 2.5rem;
  }

  .navbar-logo {
    padding: 0;
  }
}

@media (max-width: 52.5rem) {
  #navbar {
    padding: 0 2.25rem;
    grid-template-columns: 4fr 1fr;
  }
  .left-menu,
  .right-menu {
    display: none;
  }
  .hamburger {
    display: flex;
    grid-column: 2 / 3;
    justify-self: end;
  }
  .navbar-logo {
    text-align: left;
    grid-column: 1 / 2;
  }
}

@media (max-width: 48rem) {
  #navbar {
    padding: 0 1.25rem;
  }
}
</style>
